import React, { useCallback, useEffect, useState } from 'react'
import {
  capitalize,
  Chip,
  FormControl,
  MenuItem,
  Select,
  type SelectChangeEvent,
} from '@mui/material'
import { getLTIIntegration, updateDistrict } from 'providers/mutations'
import { IIngestedDistrict } from 'infrastructure/api'

type ChipColor =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'error'
  | 'info'
  | 'success'
  | 'warning'

function getIntegrationChipProps(sisType: string): {
  color: ChipColor
  variant: 'filled' | 'outlined'
} {
  switch (sisType.toLowerCase()) {
    case 'clever':
      return { color: 'primary', variant: 'filled' }
    case 'classlink':
      return { color: 'info', variant: 'filled' }
    case 'edlink':
      return { color: 'secondary', variant: 'filled' }
    case 'oneroster':
      return { color: 'success', variant: 'filled' }
    case 'manual/csv':
      return { color: 'warning', variant: 'filled' }
    default:
      return { color: 'default', variant: 'outlined' }
  }
}

const lmsTypeLabels: Record<string, string> = {
  canvas: 'Canvas',
  google_classroom: 'Google Classroom',
  schoology: 'Schoology',
  blackboard: 'Blackboard',
  powerschool: 'PowerSchool',
  other: 'Other',
}

const DistrictHeader = ({
  district,
  ingestedDistrict,
  onDistrictUpdated,
}: {
  district?: {
    name: string
    id: string
    is_verified: boolean
    created_at: string
    deleted_at?: string
    lms_type?: string
  }
  ingestedDistrict?: IIngestedDistrict
  onDistrictUpdated?: () => void
}) => {
  const [ltiIntegration, setLTIIntegration] = useState<string>('Not Connected')
  const [lmsType, setLmsType] = useState<string>('')

  useEffect(() => {
    setLmsType(district?.lms_type ?? '')
  }, [district?.lms_type])

  const fetchLTIIntegration = useCallback(async () => {
    if (district) {
      try {
        const integration = await getLTIIntegration(parseInt(district.id))
        setLTIIntegration(integration.issuer)
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log('Error getting LTI integration', err)
      }
    }
  }, [district])
  useEffect(() => {
    fetchLTIIntegration()
  }, [fetchLTIIntegration])

  const handleLmsTypeChange = useCallback(
    async (event: SelectChangeEvent<string>) => {
      if (!district) return
      const newValue = event.target.value
      setLmsType(newValue)
      try {
        await updateDistrict({
          id: Number(district.id),
          lms_type: newValue,
        })
        onDistrictUpdated?.()
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error updating LMS type', err)
        setLmsType(district.lms_type ?? '')
      }
    },
    [district, onDistrictUpdated],
  )

  if (!district) {
    return <div>Loading...</div>
  }
  const sisType = capitalize(ingestedDistrict?.sis_type ?? '')
  return (
    <div>
      <p>
        District Name: <b> {district.name} </b>
      </p>
      <p>
        District ID: <b> {district.id} </b>
      </p>
      <p>
        Is Verified: <b> {String(district.is_verified)} </b>
      </p>
      <p>
        Created At: <b> {district.created_at} </b>
      </p>
      {district.deleted_at && (
        <p>
          Deleted At: <b> {district.deleted_at} </b>
        </p>
      )}
      <p>
        Integration Type:{' '}
        <Chip
          label={sisType || 'None'}
          size="small"
          {...getIntegrationChipProps(sisType)}
          sx={{ ml: 1 }}
        />
      </p>
      <p>
        LMS Type:{' '}
        <FormControl size="small" sx={{ ml: 1, minWidth: 160 }}>
          <Select
            value={lmsType}
            onChange={handleLmsTypeChange}
            displayEmpty
            size="small"
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {Object.entries(lmsTypeLabels).map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </p>
      <p>
        Sync ID: <b> {ingestedDistrict?.district_sis_id || 'None'} </b>
      </p>
      <p>
        LTI Integration: <b> {ltiIntegration}</b>
      </p>
    </div>
  )
}

export default DistrictHeader
